export type UniversityTheme = 'default' | 'greyseal' | 'umbrella' | 'fareport' | 'octopus' | 'arden' | 'connected' | 'crosby';

export interface UniversityThemeConfig {
  name: string;
  className: string;
  colors: {
    primary: string;
    secondary: string;
  };
  cssVariables: Record<string, string>;
}

export const UNIVERSITY_THEMES: Record<UniversityTheme, UniversityThemeConfig> = {
  default: {
    name: 'Default',
    className: '',
    colors: {
      primary: '213 94% 68%',
      secondary: '0 0% 92%'
    },
    cssVariables: {}
  },
  greyseal: {
    name: 'Greyseal',
    className: 'theme-greyseal',
    colors: {
      primary: '25 87% 56%', // #f07e2f (orange)
      secondary: '232 11% 35%' // #4f5062 (grey-blue)
    },
    cssVariables: {
      '--primary': '25 87% 56%', // #f07e2f (Orange)
      '--primary-foreground': '0 0% 100%',
      '--secondary': '232 11% 35%', // #4f5062 (Grey-blue)
      '--secondary-foreground': '0 0% 100%',
      '--medical-gradient-from': '25 87% 56%', // Solid orange for medical side
      '--medical-gradient-to': '25 87% 56%', // Same orange (no gradient)
      '--no-evidence-gradient-from': '232 11% 35%', // Solid grey-blue for no-evidence side
      '--no-evidence-gradient-to': '232 11% 35%', // Same grey-blue (no gradient)
      '--medical-accent': '25 100% 97%', // Very light orange
      '--medical-accent-foreground': '25 87% 56%',
      '--no-evidence-accent': '232 20% 97%', // Very light grey-blue
      '--no-evidence-accent-foreground': '232 11% 35%',
      '--content-section': '232 20% 97%', // Very light grey-blue
      '--progress-background': '232 20% 90%',
      '--info-text': '232 15% 25%', // Dark grey-blue
      '--accent': '85 16% 54%', // #8b9e77 (Sage green)
      '--accent-foreground': '0 0% 100%'
    }
  },
  umbrella: {
    name: 'Umbrella',
    className: 'theme-umbrella',
    colors: {
      primary: '296 24% 27%', // #563557 (dark purple)
      secondary: '299 31% 64%' // #BD8BBE (light purple)
    },
    cssVariables: {
      '--primary': '296 24% 27%', // #563557 (Dark purple)
      '--primary-foreground': '0 0% 100%',
      '--secondary': '299 31% 64%', // #BD8BBE (Light purple)
      '--secondary-foreground': '0 0% 100%',
      '--medical-gradient-from': '296 24% 27%', // Solid dark purple for medical side
      '--medical-gradient-to': '296 24% 27%', // Same dark purple (no gradient)
      '--no-evidence-gradient-from': '299 31% 64%', // Solid light purple for no-evidence side
      '--no-evidence-gradient-to': '299 31% 64%', // Same light purple (no gradient)
      '--medical-accent': '296 100% 97%', // Very light purple
      '--medical-accent-foreground': '296 24% 27%',
      '--no-evidence-accent': '299 100% 95%', // Very light purple
      '--no-evidence-accent-foreground': '299 31% 64%',
      '--content-section': '299 50% 95%', // Light purple background
      '--progress-background': '299 30% 90%',
      '--info-text': '296 15% 35%', // Dark purple text
      '--accent': '189 39% 67%', // #89C3CD (Light blue/teal)
      '--accent-foreground': '0 0% 100%'
    }
  },
  fareport: {
    name: 'Fareport',
    className: 'theme-fareport',
    colors: {
      primary: '207 99% 44%', // #01A0E1 (bright blue)
      secondary: '256 40% 18%' // #261C41 (dark purple/navy)
    },
    cssVariables: {
      '--primary': '207 99% 44%', // #01A0E1 (Bright blue)
      '--primary-foreground': '0 0% 100%',
      '--secondary': '256 40% 18%', // #261C41 (Dark purple/navy)
      '--secondary-foreground': '0 0% 100%',
      '--medical-gradient-from': '207 99% 44%', // Solid bright blue for medical side
      '--medical-gradient-to': '207 99% 44%', // Same bright blue (no gradient)
      '--no-evidence-gradient-from': '256 40% 18%', // Solid dark navy for no-evidence side
      '--no-evidence-gradient-to': '256 40% 18%', // Same dark navy (no gradient)
      '--medical-accent': '207 100% 97%', // Very light blue
      '--medical-accent-foreground': '207 99% 44%',
      '--no-evidence-accent': '256 100% 97%', // Very light purple/navy
      '--no-evidence-accent-foreground': '256 40% 18%',
      '--content-section': '207 50% 95%', // Light blue background
      '--progress-background': '207 30% 90%',
      '--info-text': '256 15% 25%', // Dark navy text
      '--accent': '37 95% 65%', // #FBBB51 (Golden yellow)
      '--accent-foreground': '0 0% 100%'
    }
  },
  octopus: {
    name: 'Octopus Energy',
    className: 'theme-octopus',
    colors: {
      // Primary: Pink #FF71CF, Secondary: Core Purple #261842
      primary: '311 100% 72%',
      secondary: '251 47% 18%'
    },
    cssVariables: {
      '--primary': '311 100% 72%', // Pink
      '--primary-foreground': '0 0% 100%',
      '--secondary': '251 47% 18%', // Core Purple
      '--secondary-foreground': '0 0% 100%',
      // Gradients follow primary/secondary
      '--medical-gradient-from': '311 100% 72%',
      '--medical-gradient-to': '311 100% 72%',
      '--no-evidence-gradient-from': '251 47% 18%',
      '--no-evidence-gradient-to': '251 47% 18%',
      // Accents and surface tints
      '--medical-accent': '311 100% 96%', // Very light pink
      '--medical-accent-foreground': '311 100% 40%',
      '--no-evidence-accent': '251 40% 95%', // Very light purple tint
      '--no-evidence-accent-foreground': '251 47% 18%',
      // Surfaces and text
      '--content-section': '0 0% 100%', // Prefer white surfaces (80% white guidance)
      '--progress-background': '251 20% 90%',
      '--info-text': '210 32% 18%', // #1F2F3C
      // Accent: Legacy Blue #88B3D1
      '--accent': '204 50% 67%',
      '--accent-foreground': '0 0% 0%'
    }
  },
  arden: {
    name: 'Arden University',
    className: 'theme-arden',
    colors: {
      primary: '171 49% 59%', // #66C9BA (teal)
      secondary: '218 100% 15%' // #001D4E (deep navy)
    },
    cssVariables: {
      '--primary': '171 49% 59%', // #66C9BA
      '--primary-foreground': '0 0% 100%',
      '--secondary': '218 100% 15%', // #001D4E
      '--secondary-foreground': '0 0% 100%',
      '--medical-gradient-from': '171 49% 59%',
      '--medical-gradient-to': '171 49% 59%',
      '--no-evidence-gradient-from': '218 100% 15%',
      '--no-evidence-gradient-to': '218 100% 15%',
      '--medical-accent': '171 100% 96%',
      '--medical-accent-foreground': '171 49% 35%',
      '--no-evidence-accent': '218 40% 95%',
      '--no-evidence-accent-foreground': '218 100% 15%',
      '--content-section': '0 0% 100%',
      '--progress-background': '218 20% 90%',
      '--info-text': '218 32% 18%',
      '--accent': '46 100% 49%', // #FCBF00
      '--accent-foreground': '0 0% 0%'
    }
  },
  connected: {
    name: 'connectED',
    className: 'theme-connected',
    colors: {
      primary: '174 40% 49%', // #485488
      secondary: '228 31% 41%' // #4BB0A5
    },
    cssVariables: {
      '--primary': '174 40% 49%', // #485488
      '--primary-foreground': '0 0% 100%',
      '--secondary': '228 31% 41%', // #4BB0A5
      '--secondary-foreground': '0 0% 100%',
      '--medical-gradient-from': '174 40% 49%',
      '--medical-gradient-to': '174 40% 49%',
      '--no-evidence-gradient-from': '228 31% 41%',
      '--no-evidence-gradient-to': '228 31% 41%',
      '--medical-accent': '174 40% 96%',
      '--medical-accent-foreground': '174 40% 49%',
      '--no-evidence-accent': '228 40% 95%',
      '--no-evidence-accent-foreground': '228 31% 41%',
      '--content-section': '0 0% 100%',
      '--progress-background': '228 20% 90%',
      '--info-text': '228 25% 25%',
      '--accent': '200 64% 51%', // #319FD4
      '--accent-foreground': '0 0% 100%'
    }
  },
  crosby: {
    name: 'Crosby Training',
    className: 'theme-crosby',
    colors: {
      primary: '207 100% 26%', // #004b87 (navy)
      secondary: '185 100% 27%' // #00808b (teal)
    },
    cssVariables: {
      '--primary': '207 100% 26%', // #004b87
      '--primary-foreground': '0 0% 100%',
      '--secondary': '185 100% 27%', // #00808b
      '--secondary-foreground': '0 0% 100%',
      '--medical-gradient-from': '207 100% 26%',
      '--medical-gradient-to': '207 100% 26%',
      '--no-evidence-gradient-from': '185 100% 27%',
      '--no-evidence-gradient-to': '185 100% 27%',
      '--medical-accent': '207 60% 96%',
      '--medical-accent-foreground': '207 100% 26%',
      '--no-evidence-accent': '185 50% 95%',
      '--no-evidence-accent-foreground': '185 100% 27%',
      '--content-section': '0 0% 100%',
      '--progress-background': '207 25% 90%',
      '--info-text': '0 0% 27%', // #444444
      '--accent': '26 88% 59%', // #f38b3c (orange CTA)
      '--accent-foreground': '0 0% 100%'
    }
  }
};

/**
 * Get the current university theme from environment variables
 */
export function getCurrentUniversityTheme(): UniversityTheme {
  const themeFromEnv = process.env.NEXT_PUBLIC_UNIVERSITY_THEME as UniversityTheme;
  
  if (themeFromEnv && themeFromEnv in UNIVERSITY_THEMES) {
    return themeFromEnv;
  }
  
  return 'default';
}

/**
 * Get theme configuration for a specific university
 */
export function getThemeConfig(theme: UniversityTheme): UniversityThemeConfig {
  return UNIVERSITY_THEMES[theme] || UNIVERSITY_THEMES.default;
}

/**
 * Apply the university theme to the document
 */
export function applyUniversityTheme(theme: UniversityTheme = getCurrentUniversityTheme()) {
  if (typeof document === 'undefined') return;
  
  const config = getThemeConfig(theme);
  const html = document.documentElement;
  
  // Remove all existing theme classes
  Object.values(UNIVERSITY_THEMES).forEach(({ className }) => {
    if (className) {
      html.classList.remove(className);
    }
  });
  
  // Apply new theme class
  if (config.className) {
    html.classList.add(config.className);
  }

  // NUCLEAR OPTION: Directly set CSS variables via JavaScript
  if (config.cssVariables) {
    Object.entries(config.cssVariables).forEach(([property, value]) => {
      html.style.setProperty(property, value);
    });
  }
}

/**
 * Get theme class name for SSR
 */
export function getThemeClassName(theme: UniversityTheme = getCurrentUniversityTheme()): string {
  const config = getThemeConfig(theme);
  return config.className;
} 